import { compareAsc, compareDesc } from "date-fns";
import { groupBy, keyBy, partition } from "lodash-es";

import {
  getGroupIdFromAccessId,
  getStudentIdFromAccessId,
  isGroupAccessId,
  isStudentAccessId,
} from "../../shared/access-id";

import {
  activitySelectors,
  assessmentSelectors,
  createAppSelector,
  outcomeAssessmentSelectors,
  pageGroupSelectors,
  pageGroupUserSelectors,
  pageOutcomeSelectors,
  pageSelectors,
  sectionOutcomeSelectors,
  sectionSelectors,
  sectionUserSelectors,
  selectCurrentUserId,
  submissionSelectors,
  topicSelectors,
} from "./base";
import { selectPageGroupUsersByPageId } from "./page-groups";
import { selectPagesForCurrentSection } from "./section";
import { selectEnabledSectionUsers, selectEnabledSectionUsersByPageId } from "./section-user";
import { comparePagesByCategoryAndOrder, sortPagesWithAssessments } from "./sorting";
import { selectPagesForOutcome, selectPagesForTopic } from "./topics";
import { isIndividualSubmission } from "./type-helpers";
import type {
  FullGroupSubmissionType,
  FullIndividualSubmissionType,
  NestedAssessmentType,
  PageWithAssessmentsType,
  PagesWithOutcomeAssessmentAndSectionType,
  PagesWithOutcomeAssessmentType,
} from "./types";

import type {
  AssessmentType,
  GroupSubmissionType,
  IndividualSubmissionType,
  OutcomeAssessmentType,
  PageOutcomeType,
  PageType,
  SectionOutcomeType,
  SectionType,
  SectionUserType,
  SubmissionType,
} from "components/server-types";
import { isGroupPage, isIndividualPage, isSharedClassPage } from "utils/page-utils";

// TODO: There's extraneous data we're getting from the server, and we should fix it + clean up our selectors.
//  1. We may have allowed users to have submissions on group and individual submissions on the same page in the past.
//  We should ensure there is backend logic to validate that this can't happen, and delete any invalid data.
//  2. We are unfortunately returning submissions that are from instructors (this can happen when admins switch
//  between section user types student <-> instructor, etc.) as well as students, but we aren't filtering the
//  submission data to only show student data. There is a short-term fix in the selectSubmissionsForPage selector
//  to filter the submissions by the section_user's role type, which works for individual submissions, but not
//  group ones. We should come up for a fix for group submissions, filter them in the backend (or here), delete any
//  invalid data in our system, have a way to automatically delete this data on switch perhaps.
//  3. We should tighten up our assessment and submissions filters so that they reuse selectSubmissionsForPage when
//  they can, and resolve some of our many submission types.
//  4. There are two different ways to access assessment mode for instructors, the Assess button next to a page item
//  on the sidebar and the Assess button on the banner on the PageOverviewHeader. The assess button is calculated via
//  backend logic, while the other is calculated via frontend logic. We ensure that they are both in sync and are
//  using the same logic to calculate assessment mode.

/* *************************** Outcome Assessments **************************** */
const EMPTY_OUTCOME_ASSESSMENTS: OutcomeAssessmentType[] = [];

export const selectOutcomeAssessmentByAssessmentId = createAppSelector(
  [
    (state, assessmentId: string | null | undefined) => assessmentId,
    outcomeAssessmentSelectors.selectAll,
  ],
  (assessmentId, outcomeAssessments) => {
    if (!assessmentId) return EMPTY_OUTCOME_ASSESSMENTS;
    return outcomeAssessments.filter((oa) => oa.assessment_id === assessmentId);
  },
);

export const selectOutcomeAssessmentsByPageId = createAppSelector(
  [pageOutcomeSelectors.filterByPageId, outcomeAssessmentSelectors.selectAll],
  (pageOutcomes, outcomeAssessments) => {
    const pageOutcomeIds = new Set(pageOutcomes.map((po) => po.id));
    return outcomeAssessments.filter((oa) => pageOutcomeIds.has(oa.page_outcome_id));
  },
);

/* *************************** Assessments **************************** */
export const selectAssessmentsByPageId = createAppSelector(
  [assessmentSelectors.selectAll, submissionSelectors.filterByPageId],
  (assessments, submissions): AssessmentType[] => {
    const submissionIdsForPage = new Set(submissions.map((s) => s.id));
    return assessments.filter((assessment) => submissionIdsForPage.has(assessment.submission_id));
  },
);

export const selectHasAssessmentsForPageId = createAppSelector(
  [selectAssessmentsByPageId],
  (assessments) => assessments.length > 0,
);

export const selectAssessmentBySubmissionId = createAppSelector(
  [(state, submissionId: string | null | undefined) => submissionId, assessmentSelectors.selectAll],
  (submissionId, assessments) => {
    if (!submissionId) return null;
    return assessments.find((a) => a.submission_id === submissionId) || null;
  },
);

export const selectUserAssessments = createAppSelector(
  [
    (state, userId: string | null | undefined) => userId,
    (state, userId: string | null | undefined, sectionId?: string | null) => sectionId,
    selectEnabledSectionUsers,
    submissionSelectors.selectAll,
    assessmentSelectors.selectAll,
    outcomeAssessmentSelectors.selectAll,
    pageGroupUserSelectors.selectAll,
  ],
  (
    userId,
    sectionId,
    sectionUsers,
    submissions,
    assessments,
    outcomeAssessments,
    pageGroupUsers,
  ): NestedAssessmentType[] => {
    const filteredSectionUsers = sectionUsers.filter(
      (cu) => (!userId || cu.user_id === userId) && (!sectionId || cu.section_id === sectionId),
    );
    const sectionUserIds = new Set(filteredSectionUsers.map((cu) => cu.id));
    const filteredGroupUsers = pageGroupUsers.filter((pgu) =>
      sectionUserIds.has(pgu.section_user_id),
    );

    const mySubmissions = submissions.filter((submission) =>
      isIndividualSubmission(submission)
        ? sectionUserIds.has(submission.section_user_id)
        : filteredGroupUsers.some(
            (pgu) => pgu.page_group_id === submission.page_group_id && pgu.is_ready,
          ),
    );

    const myAssessments = assessments.filter((assessment) =>
      mySubmissions.some((s) => s.id === assessment.submission_id),
    );

    return myAssessments
      .map((assessment) => {
        const submission = mySubmissions.find((s) => s.id === assessment.submission_id);
        if (!submission) return null;
        const outcomeAssessmentsForAssessment = outcomeAssessments.filter(
          (oa) => oa.assessment_id === assessment.id,
        );
        const nestedAssessment: NestedAssessmentType = {
          ...assessment,
          submission,
          outcome_assessments: outcomeAssessmentsForAssessment,
        };
        return nestedAssessment;
      })
      .filter((assessment) => assessment !== null);
  },
);

/* *************************** Submissions **************************** */
export const selectSubmissionsByPageId = createAppSelector(
  [
    pageSelectors.selectById,
    submissionSelectors.filterByPageId,
    selectEnabledSectionUsersByPageId,
    selectPageGroupUsersByPageId,
  ],
  (page, submissions, enabledSectionUsers, pageGroupUsers) => {
    if (!page) return [];

    return submissions.filter((submission) => {
      if (
        (isGroupPage(page) && isIndividualSubmission(submission)) ||
        (isIndividualPage(page) && !isIndividualSubmission(submission))
      ) {
        return false;
      }

      if (isIndividualSubmission(submission)) {
        // If the submission is for a user that doesn't exist or is disabled, skip it.
        return enabledSectionUsers.some((cu) => cu.id === submission.section_user_id);
      }

      return pageGroupUsers.some(
        (pgu) => pgu.page_group_id === submission.page_group_id && pgu.is_ready,
      );
    });
  },
);

export const selectHasSubmissionsForPageId = createAppSelector(
  [selectSubmissionsByPageId],
  (submissions) => submissions.length > 0,
);

export const selectSortedSubmissionsByPageId = createAppSelector(
  [selectSubmissionsByPageId],
  (submissions) => submissions.toSorted((a, b) => compareDesc(a.created_at, b.created_at)),
);

/**
 * Given a submission ID, returns the official submission for the same student/group.
 * Used to determine if the current submission is "new" (created after an assessed submission).
 */
export const selectOfficialSubmissionBySubmissionId = createAppSelector(
  [submissionSelectors.selectById, submissionSelectors.selectAll],
  (submission, allSubmissions) => {
    if (!submission) return undefined;

    // Find all submissions for the same access (section_user or page_group)
    const sameAccessSubmissions = allSubmissions.filter((s) =>
      submission.section_user_id
        ? s.section_user_id === submission.section_user_id && s.page_id === submission.page_id
        : s.page_group_id === submission.page_group_id && s.page_id === submission.page_id,
    );

    // Return the official submission (the one with official_at set)
    return sameAccessSubmissions.find((s) => s.official_at);
  },
);

export const selectFullSubmissionsByPageId = createAppSelector(
  [
    selectSubmissionsByPageId,
    selectAssessmentsByPageId,
    selectOutcomeAssessmentsByPageId,
    (state) => state.section_users,
  ],
  (
    submissions,
    assessments,
    outcome_assessments,
    sectionUsersMap,
  ): FullIndividualSubmissionType[] | FullGroupSubmissionType[] => {
    return submissions.map((submission) => {
      const assessment = assessments.find((a) => a.submission_id === submission.id);
      const outcomeAssessments = outcome_assessments.filter(
        (oa) => oa.assessment_id === assessment?.id,
      );
      if (isIndividualSubmission(submission)) {
        const section_user = sectionUsersMap[submission.section_user_id];
        return {
          ...submission,
          section_user,
          assessment,
          outcome_assessments: outcomeAssessments,
        } as FullIndividualSubmissionType;
      }
      return {
        ...submission,
        assessment,
        outcome_assessments: outcomeAssessments,
      } as FullGroupSubmissionType;
    }) as FullIndividualSubmissionType[] | FullGroupSubmissionType[];
  },
);

export const selectUserSubmissionsByPageId = createAppSelector(
  [
    (state, pageId: string, selectedAccessId?: string | null) => selectedAccessId,
    pageSelectors.selectById,
    selectCurrentUserId,
    selectSubmissionsByPageId,
    selectEnabledSectionUsersByPageId,
    selectPageGroupUsersByPageId,
    pageGroupSelectors.selectEntities,
  ],
  (
    selectedAccessId,
    page,
    currentUserId,
    submissions,
    sectionUsers,
    pageGroupUsers,
    pageGroupMap,
  ): IndividualSubmissionType[] | GroupSubmissionType[] => {
    if (
      !page ||
      isSharedClassPage(page) ||
      (selectedAccessId && isIndividualPage(page) && !isStudentAccessId(selectedAccessId)) ||
      (selectedAccessId && isGroupPage(page) && !isGroupAccessId(selectedAccessId))
    ) {
      return [];
    }

    if (isIndividualPage(page)) {
      const userId = selectedAccessId ? getStudentIdFromAccessId(selectedAccessId) : currentUserId;
      const sectionUser = sectionUsers.find((cu) => cu.user_id === userId);
      if (!sectionUser) {
        return [];
      }

      return submissions.filter(
        (submission) => submission.section_user_id === sectionUser.id,
      ) as IndividualSubmissionType[];
    }

    let groupId: string | undefined;
    if (selectedAccessId) {
      groupId = getGroupIdFromAccessId(selectedAccessId);
    } else {
      const sectionUser = sectionUsers.find((cu) => cu.user_id === currentUserId);
      const pageGroupUser = pageGroupUsers.find((pgu) => {
        const group = pageGroupMap[pgu.page_group_id];
        return (
          pgu.section_user_id === sectionUser?.id && pgu.is_ready && group?.page_id === page.id
        );
      });
      groupId = pageGroupUser?.page_group_id;
    }

    return submissions.filter(
      (submission) => submission.page_group_id === groupId,
    ) as GroupSubmissionType[];
  },
);

export const selectUserFullSubmissionsByPageId = createAppSelector(
  [
    pageSelectors.selectById,
    selectUserSubmissionsByPageId,
    selectAssessmentsByPageId,
    selectEnabledSectionUsersByPageId,
    selectOutcomeAssessmentsByPageId,
  ],
  (
    page,
    submissions,
    assessments,
    sectionUsers,
    outcomeAssessments,
  ): FullIndividualSubmissionType[] | FullGroupSubmissionType[] => {
    const sectionUsersMap = keyBy(sectionUsers, (cu) => cu.id);
    if (isIndividualPage(page)) {
      return (submissions as IndividualSubmissionType[]).map((submission) => {
        const section_user = sectionUsersMap[submission.section_user_id];
        const assessment = assessments.find((a) => a.submission_id === submission.id);
        const outcome_assessments = outcomeAssessments.filter(
          (oa) => oa.assessment_id === assessment?.id,
        );
        return {
          ...submission,
          section_user,
          assessment,
          outcome_assessments,
        };
      }) as FullIndividualSubmissionType[];
    }
    return (submissions as GroupSubmissionType[]).map((submission) => {
      const assessment = assessments.find((a) => a.submission_id === submission.id);
      const outcome_assessments = outcomeAssessments.filter(
        (oa) => oa.assessment_id === assessment?.id,
      );
      return {
        ...submission,
        assessment,
        outcome_assessments,
      };
    }) as FullGroupSubmissionType[];
  },
);

export const selectUserOfficialSubmissionByPageId = createAppSelector(
  [selectUserSubmissionsByPageId],
  (submissions) => {
    return submissions.find((submission) => submission.official_at);
  },
);

export const selectAssessedSubmissionsByTopicId = createAppSelector(
  [selectPagesForTopic, assessmentSelectors.selectAll, submissionSelectors.selectAll],
  (pages, assessments, submissions) => {
    const pageIds = pages.map((page) => page.id);
    return submissions
      .filter((s) => pageIds.includes(s.page_id))
      .filter((s) => assessments.find((a) => a.submission_id === s.id));
  },
);

export const selectAssessedSubmissionsByPageId = createAppSelector(
  [selectAssessmentsByPageId, submissionSelectors.selectAll],
  (assessments, submissions) => {
    return submissions.filter((s) => assessments.find((a) => a.submission_id === s.id));
  },
);

export const selectSortedUserFullSubmissionsByPageId = createAppSelector(
  [selectUserFullSubmissionsByPageId],
  (submissions) =>
    submissions.toSorted((a: SubmissionType, b: SubmissionType) =>
      compareAsc(a.created_at, b.created_at),
    ),
);

export const selectDefaultSubmissionForUser = createAppSelector(
  [
    selectUserSubmissionsByPageId,
    (state, pageId: string, selectedAccessId?: string | null, isAssessing?: boolean) => isAssessing,
  ],
  (submissions, isAssessing): SubmissionType => {
    // TODO: move this logic to helper function
    let sortedSubmissions: SubmissionType[];

    // If the user is assessing, the default submission should be the latest submission.
    // If not, the default submission should be the official submission if it exists,
    // otherwise the latest submission.
    if (isAssessing) {
      sortedSubmissions = submissions.toSorted((a, b) => compareDesc(a.created_at, b.created_at));
    } else {
      sortedSubmissions = submissions.toSorted((a, b) =>
        a.official_at ? -1 : b.official_at ? 1 : compareDesc(a.created_at, b.created_at),
      );
    }

    return sortedSubmissions[0];
  },
);

export const selectOfficialFullSubmissionsByPageId = createAppSelector(
  [selectFullSubmissionsByPageId],
  (submissions) => submissions.filter((submission) => submission.official_at),
);

/* *************************** Pages **************************** */
export const selectPagesWithAssessmentsForUserId = createAppSelector(
  [
    (state, userId?: string | null, sectionId?: string | null) =>
      selectUserAssessments(state, userId, sectionId),
    pageSelectors.selectAll,
    activitySelectors.selectEntities,
    topicSelectors.selectEntities,
  ],
  (currentUserAssessments, pages, activityMap, topicMap): PageWithAssessmentsType[] => {
    const assessmentPages = pages.flatMap((p) => {
      const pageAssessments = currentUserAssessments.filter((ca) => ca.submission.page_id === p.id);
      if (!pageAssessments.length) {
        return [];
      }

      const [officialPageAssessments, nonOfficialPageAssessments] = partition(
        pageAssessments,
        (a) => !!a.submission.official_at,
      );

      const activity = p.activity_id ? activityMap[p.activity_id] : null;
      const topic = activity ? topicMap[activity.topic_id] : null;

      return [
        {
          ...p,
          topic,
          assessments: officialPageAssessments,
          nonOfficialAssessments: nonOfficialPageAssessments,
        },
      ];
    });
    return assessmentPages.sort(comparePagesByCategoryAndOrder);
  },
);

/**
 * This helper function creates the final page assessment objects, optionally including section title.
 */
const _getPagesWithAssessmentsForSectionOutcomeAndUser = (
  sectionOutcomeId: string,
  currentUserAssessments: NestedAssessmentType[],
  pageOutcomesForSectionOutcome: PageOutcomeType[],
  pagesForOutcome: PageType[],
  options: {
    excludeUnassessedPages?: boolean;
    sectionTitle?: string;
  } = {},
): PagesWithOutcomeAssessmentType[] | PagesWithOutcomeAssessmentAndSectionType[] => {
  const { excludeUnassessedPages = false, sectionTitle = "" } = options;
  const assessmentsByPageId = groupBy(
    currentUserAssessments,
    (assessment) => assessment.submission.page_id,
  );

  const pagesWithAssessments = pagesForOutcome
    .filter((page) => {
      if (!excludeUnassessedPages) return true;
      const pageAssessments = assessmentsByPageId[page.id] || [];
      return !!pageAssessments.find((a) => a.submission.official_at);
    })
    .map((page) => {
      const pageAssessments = assessmentsByPageId[page.id] || [];
      const officialPageAssessment = pageAssessments.find((a) => a.submission.official_at);
      const pageOutcomeForSectionOutcome = pageOutcomesForSectionOutcome.find(
        (po) => po.page_id === page.id,
      );
      const outcomeAssessment = officialPageAssessment?.outcome_assessments.find(
        (oa) => oa.page_outcome_id === pageOutcomeForSectionOutcome?.id,
      );

      const basePage = {
        ...page,
        assessment: officialPageAssessment,
        outcomeAssessment,
        nonOfficialAssessments: pageAssessments,
      };

      if (sectionTitle) {
        return {
          ...basePage,
          section_title: sectionTitle,
        } as PagesWithOutcomeAssessmentAndSectionType;
      }

      return basePage;
    });

  return pagesWithAssessments as
    | PagesWithOutcomeAssessmentType[]
    | PagesWithOutcomeAssessmentAndSectionType[];
};

export const selectPagesWithAssessmentsForSectionOutcomeAndUser = createAppSelector(
  [
    (state, sectionOutcomeId: string) => sectionOutcomeId,
    (state, sectionOutcomeId: string, userId: string | null, sectionId: string | null) =>
      selectUserAssessments(state, userId, sectionId),
    pageOutcomeSelectors.selectAll,
    selectPagesForOutcome,
  ],
  (
    sectionOutcomeId,
    currentUserAssessments,
    pageOutcomes,
    pagesForOutcome,
  ): PagesWithOutcomeAssessmentType[] => {
    const pageOutcomesForSectionOutcome = pageOutcomes.filter(
      (po) => po.section_outcome_id === sectionOutcomeId,
    );

    const pagesWithAssessments = _getPagesWithAssessmentsForSectionOutcomeAndUser(
      sectionOutcomeId,
      currentUserAssessments,
      pageOutcomesForSectionOutcome,
      pagesForOutcome,
      { excludeUnassessedPages: false },
    );

    return sortPagesWithAssessments(pagesWithAssessments);
  },
);

export const selectPagesWithAssessments = createAppSelector(
  [
    selectPagesForCurrentSection,
    submissionSelectors.selectAll,
    assessmentSelectors.selectAll,
    outcomeAssessmentSelectors.selectAll,
    (s) => s.activities,
    (s) => s.topics,
  ],
  (
    pages,
    submissions,
    assessments,
    outcomeAssessments,
    activityMap,
    topicMap,
  ): PageWithAssessmentsType[] => {
    const scoreablePages = pages.filter((p) => p.released_at);
    const pagesWithAssessments = scoreablePages.map((page) => {
      const pageSubmissions = submissions.filter((s) => s.page_id === page.id);
      const officialPageSubmissions = pageSubmissions.filter((s) => s.official_at);
      const pageAssessments = assessments.filter((a) =>
        officialPageSubmissions.some((submission) => a.submission_id === submission?.id),
      );
      const nonOfficialAssessments = pageAssessments.filter(
        (a) => !officialPageSubmissions.some((submission) => a.submission_id === submission?.id),
      );
      const nestedPageAssessments: NestedAssessmentType[] = pageAssessments
        .map((assessment) => {
          const submission = officialPageSubmissions.find((s) => s.id === assessment.submission_id);
          if (!submission) return null;
          const outcomeAssessmentsForAssessment = outcomeAssessments.filter(
            (oa) => oa.assessment_id === assessment.id,
          );
          return {
            ...assessment,
            submission,
            outcome_assessments: outcomeAssessmentsForAssessment,
          } as NestedAssessmentType;
        })
        .filter((assessment) => assessment !== null);
      const nestedNonOfficialAssessments: NestedAssessmentType[] = nonOfficialAssessments
        .map((assessment) => {
          const submission = pageSubmissions.find((s) => s.id === assessment.submission_id);
          if (!submission) return null;
          const outcomeAssessmentsForAssessment = outcomeAssessments.filter(
            (oa) => oa.assessment_id === assessment.id,
          );
          return {
            ...assessment,
            submission,
            outcome_assessments: outcomeAssessmentsForAssessment,
          } as NestedAssessmentType;
        })
        .filter((assessment) => assessment !== null);
      const activity = page.activity_id ? activityMap[page.activity_id] : null;
      const topic = activity ? topicMap[activity.topic_id] : null;

      const ret: PageWithAssessmentsType = {
        ...page,
        topic,
        assessments: nestedPageAssessments,
        nonOfficialAssessments: nestedNonOfficialAssessments,
      };
      return ret;
    });
    return pagesWithAssessments.sort(comparePagesByCategoryAndOrder);
  },
);

export const selectFullSubmissionById = createAppSelector(
  [
    submissionSelectors.selectById,
    outcomeAssessmentSelectors.selectAll,
    (state) => state.assessments,
    (state) => state.section_users,
  ],
  (
    submission,
    outcomeAssessments,
    assessmentsObj,
    sectionUsersObj,
  ): FullIndividualSubmissionType | FullGroupSubmissionType | undefined => {
    if (!submission) {
      return undefined;
    }

    const assessment = Object.values(assessmentsObj).find((a) => a.submission_id === submission.id);
    const outcome_assessments = outcomeAssessments.filter(
      (oa) => oa.assessment_id === assessment?.id,
    );
    if (isIndividualSubmission(submission)) {
      const section_user = sectionUsersObj[submission.section_user_id];
      return {
        ...submission,
        section_user,
        assessment,
        outcome_assessments,
      } as FullIndividualSubmissionType;
    }
    return {
      ...submission,
      assessment,
      outcome_assessments,
    } as FullGroupSubmissionType;
  },
);

/**
 * This selector finds all section outcomes relevant to the current user across all their sections.
 */
export const selectSectionOutcomesForUser = createAppSelector(
  [sectionOutcomeSelectors.selectAll, sectionUserSelectors.filterForCurrentUser],
  (
    sectionOutcomes,
    sectionUsersForCurrentUser,
  ): Array<{ sectionOutcome: SectionOutcomeType; sectionUser: SectionUserType }> => {
    const sectionOutcomesBySectionId = groupBy(
      sectionOutcomes,
      (sectionOutcome) => sectionOutcome.section_id,
    );

    return sectionUsersForCurrentUser.flatMap((sectionUser) => {
      const sectionOutcomesForSection = sectionOutcomesBySectionId[sectionUser.section_id] || [];
      return sectionOutcomesForSection.map((sectionOutcome) => ({
        sectionOutcome,
        sectionUser,
      }));
    });
  },
);

/**
 * This selector filters all section outcomes relevant to the current user to those that
 * match the specified outcome ID.
 */
export const selectSectionOutcomesForOutcomeAndUser = createAppSelector(
  [(state, outcomeId: string) => outcomeId, selectSectionOutcomesForUser],
  (
    outcomeId,
    allSectionOutcomesForUser,
  ): Array<{ sectionOutcome: SectionOutcomeType; sectionUser: SectionUserType }> => {
    return allSectionOutcomesForUser.filter((item) => item.sectionOutcome.outcome_id === outcomeId);
  },
);

/**
 * This helper function pre-computes lookup maps to avoid poor filtering performance in the main selector logic.
 */
const _createLookupMaps = (
  allSubmissions: SubmissionType[],
  allAssessments: AssessmentType[],
  allOutcomeAssessments: OutcomeAssessmentType[],
  allPageOutcomes: PageOutcomeType[],
  allPages: PageType[],
  allSections: SectionType[],
) => {
  const submissionsBySectionUserId = groupBy(
    allSubmissions,
    (submission) => submission.section_user_id,
  );

  const assessmentsBySubmissionId = keyBy(allAssessments, (assessment) => assessment.submission_id);
  const outcomeAssessmentsByAssessmentId = groupBy(allOutcomeAssessments, (oa) => oa.assessment_id);
  const pageOutcomesBySectionOutcome = groupBy(allPageOutcomes, (po) => po.section_outcome_id);
  const pagesBySectionId = groupBy(allPages, (page) => page.section_id);
  const sectionsById = keyBy(allSections, (section) => section.id);

  return {
    submissionsBySectionUserId,
    assessmentsBySubmissionId,
    outcomeAssessmentsByAssessmentId,
    pageOutcomesBySectionOutcome,
    pagesBySectionId,
    sectionsById,
  };
};

/**
 * This selector aggregates a user's assessment data from multiple sections for pages that are
 * linked to the specified outcome ID.
 */
export const selectPagesWithAssessmentsForOutcomeAndUser = createAppSelector(
  [
    (state, outcomeId: string) => outcomeId,
    (state, outcomeId: string) => selectSectionOutcomesForOutcomeAndUser(state, outcomeId),
    sectionSelectors.selectAll,
    pageSelectors.selectAll,
    assessmentSelectors.selectAll,
    pageOutcomeSelectors.selectAll,
    submissionSelectors.selectAll,
    outcomeAssessmentSelectors.selectAll,
  ],
  (
    outcomeId,
    sectionOutcomesForUser,
    allSections,
    allPages,
    allAssessments,
    allPageOutcomes,
    allSubmissions,
    allOutcomeAssessments,
  ): PagesWithOutcomeAssessmentAndSectionType[] => {
    if (sectionOutcomesForUser.length === 0) return [];

    const {
      submissionsBySectionUserId,
      assessmentsBySubmissionId,
      outcomeAssessmentsByAssessmentId,
      pageOutcomesBySectionOutcome,
      pagesBySectionId,
      sectionsById,
    } = _createLookupMaps(
      allSubmissions,
      allAssessments,
      allOutcomeAssessments,
      allPageOutcomes,
      allPages,
      allSections,
    );

    const pagesWithAssessments = sectionOutcomesForUser.flatMap(
      ({ sectionOutcome, sectionUser }) => {
        const section_id = sectionUser.section_id;
        const userSubmissions = submissionsBySectionUserId[sectionUser.id] || [];
        const sectionPages = pagesBySectionId[section_id] || [];
        const pageOutcomesForSectionOutcome = pageOutcomesBySectionOutcome[sectionOutcome.id] || [];
        const pageIdsForOutcome = new Set(pageOutcomesForSectionOutcome.map((po) => po.page_id));
        const pagesForOutcome = sectionPages.filter((page) => pageIdsForOutcome.has(page.id));
        const section = sectionsById[section_id];
        const currentUserAssessments: NestedAssessmentType[] = userSubmissions.flatMap(
          (submission) => {
            const assessment = assessmentsBySubmissionId[submission.id];
            if (!assessment) return [];

            const outcomeAssessments = outcomeAssessmentsByAssessmentId[assessment.id] || [];
            return [
              {
                ...assessment,
                submission,
                outcome_assessments: outcomeAssessments,
              },
            ];
          },
        );

        const pages = _getPagesWithAssessmentsForSectionOutcomeAndUser(
          sectionOutcome.id,
          currentUserAssessments,
          pageOutcomesForSectionOutcome,
          pagesForOutcome,
          { excludeUnassessedPages: true, sectionTitle: section?.title || "" },
        );

        return pages as PagesWithOutcomeAssessmentAndSectionType[];
      },
    );

    return sortPagesWithAssessments(pagesWithAssessments);
  },
);

export const selectAssessedPageOutcomesBySectionOutcome = createAppSelector(
  [
    (state, sectionOutcomeId: string) => sectionOutcomeId,
    pageOutcomeSelectors.selectAll,
    outcomeAssessmentSelectors.selectAll,
  ],
  (sectionOutcomeId, pageOutcomes, outcomeAssessments) => {
    const pageOutcomesForSectionOutcome = pageOutcomes.filter(
      (po) => po.section_outcome_id === sectionOutcomeId,
    );

    return pageOutcomesForSectionOutcome.filter((po) => {
      return outcomeAssessments.some((oa) => oa.page_outcome_id === po.id);
    });
  },
);
